import { DragEvent, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Modal from '@/components/Modal';
import { WarningMessage } from '@/components/Message/WarningMessage';
import { TargetIcon } from '@/components/SvgIcons/TargetIcon';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { useGetCompaniesSelectOptions } from '@/hooks/company.hooks';
import { useImportVehicles } from '@/hooks/vehicule.hooks';
import { downloadTemplateVehiculeImport } from '@/utils/file.utils';
import { hideModal } from '@/utils/modal.utils';
import { mapVehiclesImport } from '@/utils/map.utils';
import { Radio } from '@/components/Controls/Radio';
import { VehiculeOwnerType } from '@/api/entities/vehicule';
import { Select } from '@/components/Controls/Select';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { TFunction } from 'i18next';
import {
  IconFileTypeCsv,
  IconFileSpreadsheet,
  IconExclamationCircleFilled,
} from '@tabler/icons-react';
import { Trans } from 'react-i18next';
import Loader from '@/components/Loading/Loader';
import { twMerge } from 'tailwind-merge';

interface VehiculeImportModalProps {
  t: TFunction<undefined>;
  id?: string;
}

export const VehiculeImportModal = ({ t, id }: VehiculeImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [ownerType, setOwnerType] = useState<VehiculeOwnerType>(null);
  const [vehiculeOwner, setVehiculeOwner] = useState<TVehiculeSchema | null>(
    null,
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [parsedVehicleData, setParsedVehicleData] = useState<object[] | null>(
    null,
  );
  const { mutate: importVehicles, isPending: isPendingImportVehicles } =
    useImportVehicles({
      t,
      handleSuccess: () => {
        handleClearAll();
      },
    });

  const { getGroupsSelectOptions, isLoadingAllGroups } =
    useGetGroupsSelectOptions();
  const { getCompaniesSelectOptions, isLoadingAllCompanies } =
    useGetCompaniesSelectOptions();

  const isLoadingAll = isLoadingAllGroups || isLoadingAllCompanies;

  const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

  const handleFile = (file: File | null) => {
    setFileName(file ? file.name : null);
    setErrorMessages([]);
    setParsedVehicleData(null);

    if (!file) return;

    const isValidFormat = ACCEPTED_EXTENSIONS.some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );
    if (!isValidFormat) {
      setErrorMessages([`${t('common:invalidFileFormat')}.`]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      // const workbook = file.name.match(/\.csv$/i)
      //   ? XLSX.read(
      //       new TextDecoder().decode(data as ArrayBuffer).replace(/;/g, ','),
      //       { type: 'string' }
      //     )
      //   : XLSX.read(data, { type: 'array' });
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet) as object[];
      setParsedVehicleData(jsonData);

      const rawRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
      });
      const headerLength = rawRows[0]?.length ?? 0;
      const hasColumnMismatch = rawRows
        .slice(1)
        .some((row) => row.length !== headerLength);

      if (hasColumnMismatch) {
        setErrorMessages([
          `${t('common:columnCountMismatch')}. ${t('common:pleaseCorrectYourFile')}`,
        ]);
        return;
      }

      const {
        hasMissingBrand,
        hasMissingModel,
        hasDuplicateLicensePlateNumber,
        hasDuplicateVin,
        hasInvalidYear,
        hasInvalidMileage,
      } = mapVehiclesImport(jsonData, vehiculeOwner);

      const errors: string[] = [];
      if (hasMissingBrand) {
        errors.push(
          `${t('common:brandRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasMissingModel) {
        errors.push(
          `${t('common:modelRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasDuplicateLicensePlateNumber) {
        errors.push(`${t('common:duplicateLicensePlateNumbers')}.`);
      }
      if (hasDuplicateVin) {
        errors.push(`${t('common:duplicateVins')}.`);
      }
      if (hasInvalidYear) {
        errors.push(
          `${t('common:yearOfFirstRegistrationMustBeCorrectYear')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasInvalidMileage) {
        errors.push(
          `${t('common:mileageMustBeANumber')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      setErrorMessages(errors);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleFile(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] || null;
    handleFile(file);
  };

  const handleClickBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleConfirm = () => {
    if (!parsedVehicleData || errorMessages.length > 0) return;

    const normalizedOwner =
      vehiculeOwner &&
      !vehiculeOwner?.ownerGroupId &&
      !vehiculeOwner?.ownerCompanyId
        ? { ...vehiculeOwner, ownerType: null }
        : vehiculeOwner;
    const { vehicles } = mapVehiclesImport(parsedVehicleData, normalizedOwner);
    importVehicles(vehicles as TVehiculeSchema[]);
    hideModal(`#${id}`);
  };

  const handleClearAll = () => {
    setFileName(null);
    setErrorMessages([]);
    setParsedVehicleData(null);
    setOwnerType(null);
    setVehiculeOwner(null);
  };

  return (
    <>
      <Modal
        id={id}
        title={t('importVehicules')}
        size="medium"
        showSecondBtn
        secondBtnLabel={t('common:confirm')}
        containerClassName="top-6"
        bodyClassName="px-6 py-5 m-0"
        disabledSubmitButton={!parsedVehicleData || errorMessages.length > 0}
        onClickSecondBtn={handleConfirm}
        onClose={handleClearAll}
        onClickCancelBtn={handleClearAll}
        onClickOutsideContent={handleClearAll}
        onEscapePress={handleClearAll}
      >
        <div className="flex flex-col border border-gray-E5E5E5 px-5 pt-4 pb-6 rounded-lg mb-7.5 max-sm:px-3">
          <div className="flex items-start text-gray-700 text-md mb-5 max-sm:text-sm max-sm:leading-tight">
            <span className="font-semibold py-[0.5px] pl-1.25 pr-1.5 bg-[var(--purple-custom)] text-white text-sm rounded mr-1.75">
              1
            </span>
            <span className="text-black">
              {t('selectVehiclesOwner')}
              &nbsp;
              <span>-</span>
              &nbsp;
              <span className="text-gray-600">({t('common:optional')})</span>
              <span className="ml-1.5 text-black font-semibold">:</span>
            </span>
          </div>
          <div className="flex flex-col items-center">
            <Radio
              name="owner-type"
              radioListClassName="flex flex-row gap-6"
              items={[
                {
                  displayedText: 'Groupe',
                  value: VehiculeOwnerType.GROUP,
                },
                {
                  displayedText: 'Société',
                  value: VehiculeOwnerType.COMPANY,
                },
              ]}
              value={ownerType}
              onChange={(value) => {
                setOwnerType(value.target.value as VehiculeOwnerType);
                setVehiculeOwner({
                  ownerType: value.target.value as VehiculeOwnerType,
                  ownerGroupId: null,
                  ownerCompanyId: null,
                });
              }}
            />
            {ownerType && (
              <div className="mt-4">
                <Select
                  selectClassName="w-[200px]"
                  placeholder={
                    ownerType === VehiculeOwnerType.GROUP
                      ? t('common:chooseTheGroup')
                      : t('common:chooseTheCompany')
                  }
                  options={
                    ownerType === VehiculeOwnerType.GROUP
                      ? getGroupsSelectOptions()
                      : getCompaniesSelectOptions()
                  }
                  value={
                    vehiculeOwner?.ownerGroupId ||
                    vehiculeOwner?.ownerCompanyId ||
                    ''
                  }
                  onChange={(e) => {
                    if (ownerType === VehiculeOwnerType.GROUP)
                      setVehiculeOwner((prev) => ({
                        ...prev,
                        ownerGroupId: e.target.value as VehiculeOwnerType,
                      }));
                    if (ownerType === VehiculeOwnerType.COMPANY)
                      setVehiculeOwner((prev) => ({
                        ...prev,
                        ownerCompanyId: e.target.value as VehiculeOwnerType,
                      }));
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="ml-5 flex items-start text-black text-md mb-5 max-sm:text-sm max-sm:leading-tight">
          <span className="font-semibold py-[1px] px-1.25 bg-[var(--purple-custom)] text-white text-sm rounded mr-1.75">
            2
          </span>
          <div>
            <span>
              <Trans
                i18nKey={t('common:importCSVOrExcelFile')}
                components={{
                  span: <span className="text-primary font-bold" />,
                }}
              />
            </span>
            &nbsp;
            <span
              className="text-pink-F20089 font-medium hover:cursor-pointer hover:underline"
              onClick={downloadTemplateVehiculeImport}
            >
              {t('common:byClickingHere')?.toLowerCase()}
            </span>
          </div>
        </div>

        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="w-full flex flex-col justify-center items-center border-2 border-gray-300 border-dashed rounded-lg"
        >
          {fileName ? (
            <div className="p-8 text-center">
              {fileName.match(/\.csv$/i) ? (
                <IconFileTypeCsv
                  stroke={2}
                  className="w-10 h-10 text-primary mx-auto"
                />
              ) : fileName.match(/\.xls$/i) || fileName.match(/\.xlsx$/i) ? (
                <IconFileSpreadsheet
                  stroke={2}
                  className="w-10 h-10 text-primary mx-auto"
                />
              ) : (
                <IconExclamationCircleFilled
                  stroke={2}
                  className="w-10 h-10 text-red-FF2929 mx-auto"
                />
              )}
              <div className="mt-3 text-gray-1E1E1E font-medium max-sm:leading-tight">
                {fileName}
              </div>
              <div
                className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm text-center inline-block hover:cursor-pointer max-sm:mt-5 max-sm:leading-tight"
                onClick={handleClickBrowse}
              >
                {t('common:chooseAnotherFile')}
              </div>
            </div>
          ) : (
            <div className="p-8">
              <div className="flex justify-center items-center">
                <TargetIcon className="w-10 h-10 [&>path]:fill-gray-1E1E1E" />
              </div>
              <div className="mt-3 flex justify-center text-gray-1E1E1E text-xl font-medium text-center">
                {t('common:dragAndDropFile')}
              </div>
              <div className="mt-3 flex justify-center text-gray-1E1E1E text-sm uppercase">
                {t('common:or')}
              </div>
              <div
                className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm text-center hover:cursor-pointer max-sm:leading-tight"
                onClick={handleClickBrowse}
              >
                {t('common:searchFolderAndUploadFile')}
              </div>
              <div className="mt-3 text-gray-600 text-2sm text-center">
                {`${t('common:allowedFormats')} : CSV, XLSX, XLS`}
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(',')}
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
        <div
          className={twMerge('space-y-2', errorMessages?.length > 0 && 'mt-3')}
        >
          {errorMessages?.map((msg, index) => (
            <WarningMessage
              key={index}
              containerClassName="pt-3 pb-2 w-full max-w-none justify-start mt-0 mb-0"
              textClassName="mt-[1px]"
              message={msg}
            />
          ))}
        </div>
        {/* <WarningMessage
        containerClassName="w-full max-w-none justify-start"
        message={errorMessages?.join('\n')}
      /> */}
      </Modal>
      {(isLoadingAll || isPendingImportVehicles) && <Loader />}
    </>
  );
};
